import React, { useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  createSupportVideo,
  deleteSupportVideo,
  fetchSupportVideos,
  SupportVideo,
} from "../../../services/support-service";
import { SnackbarContext } from "../../../config/hooks/use-toast";
import { RootState } from "../../../redux/store/store";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlayCircle,
  faLink,
  faVideo,
  faPlus,
  faTrash,
  faTimes,
  faEnvelope,
  faPhone,
} from "@fortawesome/free-solid-svg-icons";
import {
  faFacebook,
  faTwitter,
  faInstagram,
  faLinkedin,
  faYoutube,
  faWhatsapp,
} from "@fortawesome/free-brands-svg-icons";

const extractYouTubeId = (url: string): string | null => {
  const regex =
    /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/;
  const match = url.match(regex);
  return match ? match[1] : null;
};

type EnrichedVideo = SupportVideo & { ytId: string | null; thumbnail?: string };

const SupportPage: React.FC = () => {
  const snackbar = useContext(SnackbarContext);
  const roleId = useSelector(
    (state: RootState) => state?.user?.userData?.role?.id,
  );
  const roleName = useSelector(
    (state: RootState) => state?.user?.userData?.role?.name,
  );
  const isAdmin = roleId === 1 && roleName === "ROLE_ADMIN";

  const [videos, setVideos] = useState<SupportVideo[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedVideo, setSelectedVideo] = useState<EnrichedVideo | null>(null);
  const [form, setForm] = useState({ title: "", youtubeUrl: "" });
  const [saving, setSaving] = useState<boolean>(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);

  const loadVideos = async () => {
    try {
      setLoading(true);
      const data = await fetchSupportVideos();
      setVideos(data || []);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        "Impossible de récupérer les vidéos de support.";
      snackbar?.showMessage?.("Erreur", message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isAdmin) {
      snackbar?.showMessage?.(
        "Permission refusée",
        "Seul l'administrateur peut ajouter des vidéos.",
        "error",
      );
      return;
    }

    const title = form.title.trim();
    const url = form.youtubeUrl.trim();

    if (!title || !url) {
      snackbar?.showMessage?.(
        "Champs requis",
        "Ajoutez un titre et un lien YouTube valide.",
        "warning",
      );
      return;
    }

    const youtubeId = extractYouTubeId(url);
    if (!youtubeId) {
      snackbar?.showMessage?.(
        "Lien invalide",
        "Merci de saisir un lien YouTube valide.",
        "warning",
      );
      return;
    }

    try {
      setSaving(true);
      const created = await createSupportVideo(title, url);
      setVideos((prev) => [created, ...prev]);
      setForm({ title: "", youtubeUrl: "" });
      setShowAddModal(false);
      snackbar?.showMessage?.(
        "Vidéo ajoutée",
        "Le tutoriel a été ajouté au support.",
        "success",
      );
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.errors?.[0] ||
        "Impossible d'ajouter cette vidéo.";
      snackbar?.showMessage?.("Erreur", message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (videoId: number) => {
    if (!isAdmin) return;
    setDeletingId(videoId);
    try {
      await deleteSupportVideo(videoId);
      setVideos((prev) => prev.filter((video) => video.id !== videoId));
      snackbar?.showMessage?.(
        "Supprimé",
        "La vidéo a été retirée du support.",
        "success",
      );
    } catch (error: any) {
      const message =
        error?.response?.data?.message || "Suppression impossible pour le moment.";
      snackbar?.showMessage?.("Erreur", message, "error");
    } finally {
      setDeletingId(null);
    }
  };

  const videoGrid = useMemo(
    () =>
      videos.map((video) => {
        const ytId = extractYouTubeId(video.youtubeUrl);
        const thumbnail = ytId
          ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`
          : undefined;
        return { ...video, ytId, thumbnail };
      }),
    [videos],
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-slate-50 to-emerald-50/30">
      {/* Modern Header */}
      {/* <div className="relative overflow-hidden bg-primary">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE2YzAtNC40MTggMy41ODItOCA4LThzOCAzLjU4MiA4IDgtMy41ODIgOC04IDgtOC0zLjU4Mi04LTh6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-20"></div>
        <div className="relative px-6 py-12 mx-auto max-w-7xl">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 text-xs font-semibold text-white rounded-full bg-white/20 backdrop-blur-sm">
                <FontAwesomeIcon icon={faVideo} className="w-3 h-3" />
                <span>Centre d'aide</span>
              </div>
              <h1 className="mb-3 text-4xl font-bold tracking-tight text-white md:text-5xl">
                Tutoriels Vidéo
              </h1>
              <p className="max-w-2xl text-lg text-white/90">
                Découvrez nos guides vidéo pour maîtriser toutes les fonctionnalités de la plateforme Fok Nje7ek
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-4 px-6 py-4 border bg-white/10 backdrop-blur-md rounded-2xl border-white/20">
                <div className="flex items-center justify-center w-14 h-14 bg-white/20 rounded-xl">
                  <FontAwesomeIcon icon={faVideo} className="text-white w-7 h-7" />
                </div>
                <div>
                  <div className="text-3xl font-bold text-white">{videos.length}</div>
                  <div className="text-sm text-white/90">Tutoriels disponibles</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> */}

      {/* Videos Grid Section */}
      <div className="px-6 py-12 mx-auto max-w-7xl">
        <div className="mb-8">
          <h2 className="mb-2 text-3xl font-bold text-gray-900">
            Tous les tutoriels
          </h2>
          <p className="text-gray-600">
            Cliquez sur une vidéo pour la regarder en plein écran
          </p>
        </div>

        {loading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className="h-72 bg-gradient-to-br from-gray-100 to-gray-200 animate-pulse rounded-2xl"
              />
            ))}
          </div>
        ) : videoGrid.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-16 text-center bg-white border-2 border-gray-200 border-dashed rounded-3xl">
            <div className="flex items-center justify-center w-20 h-20 mb-4 rounded-full bg-primary/10">
              <FontAwesomeIcon
                icon={faVideo}
                className="w-10 h-10 text-primary"
              />
            </div>
            <p className="mb-2 text-xl font-bold text-gray-900">
              Aucun tutoriel disponible
            </p>
            <p className="max-w-md text-gray-600">
              {isAdmin 
                ? "Cliquez sur le bouton + pour ajouter votre première vidéo" 
                : "Les tutoriels vidéo seront bientôt disponibles"}
            </p>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {videoGrid.map((video) => (
              <div
                key={video.id}
                className="relative overflow-hidden transition-all duration-300 bg-white border border-gray-200 shadow-sm cursor-pointer group rounded-2xl hover:-translate-y-2 hover:shadow-2xl hover:border-primary"
                onClick={() => setSelectedVideo(video)}
              >
                <div className="relative w-full overflow-hidden aspect-video">
                  {video.thumbnail ? (
                    <>
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="object-cover w-full h-full transition duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                      <div className="absolute inset-0 flex items-center justify-center transition-opacity duration-300 opacity-0 group-hover:opacity-100">
                        <div className="flex items-center justify-center w-16 h-16 transition-transform duration-300 transform rounded-full shadow-2xl bg-white/95 group-hover:scale-110">
                          <FontAwesomeIcon icon={faPlayCircle} className="w-8 h-8 text-primary" />
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center justify-center w-full h-full bg-primary/5">
                      <FontAwesomeIcon icon={faVideo} className="w-12 h-12 text-primary/40" />
                    </div>
                  )}
                  <div className="absolute top-3 right-3">
                    <div className="px-3 py-1 text-xs font-semibold text-white rounded-full bg-black/50 backdrop-blur-sm">
                      Tutoriel
                    </div>
                  </div>
                </div>

                <div className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="mb-1 text-base font-bold text-gray-900 transition-colors line-clamp-2 group-hover:text-primary">
                        {video.title}
                      </h3>
                      <p className="flex items-center gap-1 text-xs text-gray-500">
                        <span>Ajouté le {new Date(video.createdAt).toLocaleDateString('fr-FR')}</span>
                      </p>
                    </div>
                    {isAdmin && (
                      <Tooltip title="Supprimer">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(video.id);
                          }}
                          disabled={deletingId === video.id}
                          className="!opacity-0 group-hover:!opacity-100 !transition-opacity"
                        >
                          <FontAwesomeIcon
                            icon={faTrash}
                            className="w-4 h-4 text-rose-500 hover:text-rose-600"
                          />
                        </IconButton>
                      </Tooltip>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Contact Section */}
      <div className="px-6 pb-12 mx-auto max-w-7xl">
        <div className="overflow-hidden border border-gray-100 shadow-xl rounded-3xl bg-gradient-to-br from-slate-50 to-white">
          <div className="px-8 py-10">
            <div className="flex items-center gap-3 mb-8">
              <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-primary/10">
                <FontAwesomeIcon icon={faEnvelope} className="w-7 h-7 text-primary" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900">Contactez-nous</h2>
                <p className="text-gray-600">Nous sommes là pour vous aider</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-6">
              {/* Email */}
              <div className="flex-1 min-w-[280px] p-6 transition-all duration-300 bg-white border-2 border-gray-200 shadow-sm group rounded-2xl hover:border-primary hover:shadow-lg">
                <div className="flex items-center justify-center mb-4 transition-colors w-14 h-14 rounded-xl bg-primary/10 group-hover:bg-primary/20">
                  <FontAwesomeIcon icon={faEnvelope} className="w-7 h-7 text-primary" />
                </div>
                <p className="mb-2 text-sm font-semibold tracking-wide text-gray-500 uppercase">Email</p>
                <a 
                  href="mailto:foknjeikacademy@gmail.com" 
                  className="text-lg font-bold text-gray-900 transition-colors hover:text-primary"
                >
                  foknjeikacademy@gmail.com
                </a>
              </div>

              {/* Phone */}
              <div className="flex-1 min-w-[280px] p-6 transition-all duration-300 bg-white border-2 border-gray-200 shadow-sm group rounded-2xl hover:border-primary hover:shadow-lg">
                <div className="flex items-center justify-center mb-4 transition-colors w-14 h-14 rounded-xl bg-primary/10 group-hover:bg-primary/20">
                  <FontAwesomeIcon icon={faPhone} className="w-7 h-7 text-primary" />
                </div>
                <p className="mb-2 text-sm font-semibold tracking-wide text-gray-500 uppercase">Téléphone</p>
                <a 
                  href="tel:+21651347528" 
                  className="text-lg font-bold text-gray-900 transition-colors hover:text-primary"
                >
                  +216 51 347 528
                </a>
              </div>

              {/* Social Media */}
              <div className="flex-1 min-w-[280px] p-6 transition-all duration-300 bg-white border-2 border-gray-200 shadow-sm group rounded-2xl hover:border-primary hover:shadow-lg">
                <div className="flex items-center justify-center mb-4 transition-colors w-14 h-14 rounded-xl bg-primary/10 group-hover:bg-primary/20">
                  <FontAwesomeIcon icon={faFacebook} className="w-7 h-7 text-primary" />
                </div>
                <p className="mb-3 text-sm font-semibold tracking-wide text-gray-500 uppercase">Réseaux sociaux</p>
                <div className="flex gap-3">
                  <a 
                    href="https://www.facebook.com/people/%D9%81%D9%83-%D9%86%D8%AC%D8%A7%D8%AD%D9%83/100069589923551/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-center text-gray-700 transition-all duration-300 bg-gray-100 w-11 h-11 rounded-xl hover:bg-primary hover:text-white hover:scale-110 hover:shadow-md"
                  >
                    <FontAwesomeIcon icon={faFacebook} className="w-5 h-5" />
                  </a>
                  <a 
                    href="https://www.instagram.com/fok.nje7ek/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-center text-gray-700 transition-all duration-300 bg-gray-100 w-11 h-11 rounded-xl hover:bg-primary hover:text-white hover:scale-110 hover:shadow-md"
                  >
                    <FontAwesomeIcon icon={faInstagram} className="w-5 h-5" />
                  </a>
                  <a 
                    href="https://www.youtube.com/@foknje7ik" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-center text-gray-700 transition-all duration-300 bg-gray-100 w-11 h-11 rounded-xl hover:bg-primary hover:text-white hover:scale-110 hover:shadow-md"
                  >
                    <FontAwesomeIcon icon={faYoutube} className="w-5 h-5" />
                  </a>
                  <a 
                    href="https://wa.me/21651347528" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-center text-gray-700 transition-all duration-300 bg-gray-100 w-11 h-11 rounded-xl hover:bg-primary hover:text-white hover:scale-110 hover:shadow-md"
                  >
                    <FontAwesomeIcon icon={faWhatsapp} className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Add Button - Only visible for admins */}
      {isAdmin && (
        <div className="fixed z-[9999] bottom-8 right-8">
          <Tooltip title="Ajouter une vidéo" placement="left">
            <button
              onClick={() => setShowAddModal(true)}
              className="relative flex items-center justify-center w-20 h-20 text-white transition-all duration-300 rounded-full shadow-2xl group bg-primary hover:scale-110 hover:shadow-primary/50"
              style={{ backgroundColor: 'var(--primary-color, #10b981)' }}
            >
              <div className="absolute inset-0 transition-opacity duration-300 rounded-full opacity-0 bg-white/20 blur group-hover:opacity-100"></div>
              <FontAwesomeIcon icon={faPlus} className="relative z-10 w-8 h-8" />
              <div className="absolute inset-0 rounded-full opacity-75 animate-ping" style={{ backgroundColor: 'var(--primary-color, #10b981)' }}></div>
            </button>
          </Tooltip>
        </div>
      )}

      {/* Add Video Modal */}
      <Dialog
        open={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setForm({ title: "", youtubeUrl: "" });
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          className: "!rounded-3xl overflow-hidden"
        }}
      >
        <div className="relative overflow-hidden bg-primary">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE2YzAtNC40MTggMy41ODItOCA4LThzOCAzLjU4MiA4IDgtMy41ODIgOC04IDgtOC0zLjU4Mi04LTh6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30"></div>
          <DialogTitle className="relative">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 text-white">
                  <div className="flex items-center justify-center w-12 h-12 bg-white/20 rounded-xl backdrop-blur-sm">
                    <FontAwesomeIcon icon={faPlayCircle} className="w-6 h-6" />
                  </div>
                <div>
                  <div className="text-xl font-bold">Nouveau tutoriel</div>
                  <div className="text-sm text-white/80">Ajoutez une vidéo YouTube</div>
                </div>
              </div>
              <IconButton
                size="small"
                onClick={() => {
                  setShowAddModal(false);
                  setForm({ title: "", youtubeUrl: "" });
                }}
                className="!text-white/80 hover:!text-white hover:!bg-white/10"
              >
                <FontAwesomeIcon icon={faTimes} />
              </IconButton>
            </div>
          </DialogTitle>
        </div>
        <DialogContent className="!mt-6 !px-6 !pb-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <TextField
              label="Titre de la vidéo"
              value={form.title}
              onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
              fullWidth
              required
              autoFocus
              variant="outlined"
            />
            <TextField
              label="Lien YouTube"
              value={form.youtubeUrl}
              onChange={(e) =>
                setForm((p) => ({ ...p, youtubeUrl: e.target.value }))
              }
              fullWidth
              required
              variant="outlined"
              placeholder="https://www.youtube.com/watch?v=..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FontAwesomeIcon icon={faLink} className="text-emerald-500" />
                  </InputAdornment>
                ),
              }}
            />
            <div className="flex justify-end gap-3 pt-6">
              <Button
                onClick={() => {
                  setShowAddModal(false);
                  setForm({ title: "", youtubeUrl: "" });
                }}
                disabled={saving}
                className="!text-gray-600 !px-6 !py-3 !rounded-xl hover:!bg-gray-100 !font-semibold"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={saving}
                startIcon={<FontAwesomeIcon icon={faPlus} />}
                className="!rounded-xl !bg-primary !px-8 !py-3 !font-bold !text-white hover:!bg-primary/90 !shadow-lg hover:!shadow-xl !transform hover:!scale-105 !transition-all"
              >
                {saving ? "Ajout en cours..." : "Ajouter la vidéo"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Video Player Modal */}
      <Dialog
        open={!!selectedVideo}
        onClose={() => setSelectedVideo(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          className: "!rounded-3xl overflow-hidden !m-4"
        }}
      >
        {selectedVideo && (
          <>
            <div className="relative bg-gradient-to-r from-gray-900 to-gray-800">
              <DialogTitle className="!py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 text-white">
                    <FontAwesomeIcon icon={faPlayCircle} className="w-5 h-5 text-white" />
                    <span className="text-lg font-bold line-clamp-1">
                      {selectedVideo.title}
                    </span>
                  </div>
                  <IconButton 
                    size="small" 
                    onClick={() => setSelectedVideo(null)}
                    className="!text-white/70 hover:!text-white hover:!bg-white/10"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </IconButton>
                </div>
              </DialogTitle>
            </div>
            <DialogContent className="!p-0 bg-black">
              {selectedVideo.ytId ? (
                <div className="w-full aspect-video">
                  <iframe
                    title={selectedVideo.title}
                    src={`https://www.youtube.com/embed/${selectedVideo.ytId}?autoplay=1&rel=0`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full border-0"
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center p-12 text-center">
                  <FontAwesomeIcon icon={faVideo} className="w-16 h-16 mb-4 text-gray-600" />
                  <p className="mb-2 text-lg font-semibold text-white">Vidéo indisponible</p>
                  <p className="text-gray-400">Le lien YouTube n'est pas valide</p>
                </div>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </div>
  );
};

export default SupportPage;
